import { AxiosError } from 'axios';
import { formatDateToLocal } from '@safetyhub/shared';
import {
  getDeleteApproval,
  processDeleteApproval,
  type DeleteApprovalInfo,
} from '../../lib/reports';
import { DeleteApprovalActionModal } from '../../components/reports/DeleteApprovalActionModal';
import {
  ExternalSignatureShell,
  type LoaderResult,
} from '../../components/external-signature/ExternalSignatureShell';

type DeleteResult = { action: 'APPROVE' | 'REJECT' };

async function loadInfo(token: string): Promise<LoaderResult<DeleteApprovalInfo>> {
  try {
    const info = await getDeleteApproval(token);
    return { ok: true, info };
  } catch (err) {
    const axiosErr = err as AxiosError<{
      error?: { code?: string; message?: string; status?: string };
    }>;
    const errorData = axiosErr.response?.data?.error;

    if (errorData?.code === 'ALREADY_PROCESSED') {
      return {
        ok: false,
        error: {
          code: 'ALREADY_PROCESSED',
          message: errorData.message ?? '이미 처리된 요청입니다.',
          status: errorData.status,
        },
      };
    }
    if (axiosErr.response?.status === 404) {
      return {
        ok: false,
        error: { code: 'TOKEN_NOT_FOUND', message: '유효하지 않거나 만료된 링크입니다.' },
      };
    }
    return {
      ok: false,
      error: {
        code: 'NETWORK_ERROR',
        message: '서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.',
      },
    };
  }
}

const ERROR_TITLES: Record<string, string> = {
  TOKEN_NOT_FOUND: '링크를 찾을 수 없습니다',
  TOKEN_EXPIRED: '링크가 만료되었습니다',
  NETWORK_ERROR: '연결 오류',
  PROCESS_ERROR: '처리 오류',
};

function renderAlreadyProcessed(status: string | undefined) {
  const statusConfig: Record<
    string,
    { iconText: string; iconBg: string; iconColor: string; title: string; desc: string }
  > = {
    APPROVED: {
      iconText: '\u2713',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      title: '이미 처리됨',
      desc: '이 삭제 요청은 이미 승인되어 보고서가 삭제되었습니다.',
    },
    REJECTED: {
      iconText: '\u26A0',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      title: '이미 처리됨',
      desc: '이 삭제 요청은 이미 거절되었습니다.',
    },
    CANCELLED: {
      iconText: '\u26A0',
      iconBg: 'bg-ink-100',
      iconColor: 'text-ink-500',
      title: '취소된 요청',
      desc: '이 삭제 요청은 요청자에 의해 취소되었습니다.',
    },
  };
  const cfg = statusConfig[status ?? 'CANCELLED'] ?? statusConfig.CANCELLED;

  return (
    <div className="min-h-screen bg-ink-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-card shadow-lg p-8 text-center">
        <div
          className={`w-16 h-16 mx-auto mb-4 rounded-full ${cfg.iconBg} flex items-center justify-center`}
        >
          <span className={`text-2xl ${cfg.iconColor}`}>{cfg.iconText}</span>
        </div>
        <h1 className="text-xl font-semibold text-ink-900 mb-2">{cfg.title}</h1>
        <p className="text-ink-600 mb-6">{cfg.desc}</p>
        <div className="text-sm text-ink-500">
          <p>문제가 지속되면 담당자에게 문의해주세요.</p>
        </div>
      </div>
    </div>
  );
}

export default function DeleteApprovalPage() {
  return (
    <ExternalSignatureShell<DeleteApprovalInfo, DeleteResult>
      loader={loadInfo}
      extractReport={(info) => info.report}
      toolbar={{
        badgeLabel: () => '삭제 승인 요청',
        badgeTone: 'rose',
        title: (info) => info.report.title,
        subtitle: (info) =>
          `${info.report.site.name} | ${info.requesterName}님 요청 | ${formatDateToLocal(new Date(info.createdAt))}`,
        actionLabel: '삭제 승인/거절',
        actionButtonClassName: 'bg-red-600 hover:bg-red-700',
      }}
      loadingMessage="삭제 요청 정보를 불러오는 중..."
      processingMessage="삭제 요청을 처리하고 있습니다..."
      errorTitleMap={ERROR_TITLES}
      renderCustomError={(error) =>
        error.code === 'ALREADY_PROCESSED' ? renderAlreadyProcessed(error.status) : null
      }
      renderModal={({ info, token, isOpen, onClose, onSubmitted, setProcessing, setError }) => (
        <DeleteApprovalActionModal
          isOpen={isOpen}
          onClose={onClose}
          requesterName={info.requesterName}
          reason={info.reason}
          onSubmit={async (action, comment) => {
            setProcessing();
            try {
              await processDeleteApproval(token, action, comment);
              onSubmitted({ action });
            } catch {
              setError({
                code: 'PROCESS_ERROR',
                message: '처리 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
              });
            }
          }}
        />
      )}
      renderSuccess={(result) => {
        const isApproved = result?.action === 'APPROVE';
        return (
          <div className="min-h-screen bg-ink-50 flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-white rounded-card shadow-lg p-8 text-center">
              <div
                className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                  isApproved ? 'bg-green-100' : 'bg-orange-100'
                }`}
              >
                <span
                  className={`text-2xl ${isApproved ? 'text-green-600' : 'text-orange-600'}`}
                >
                  {isApproved ? '\u2713' : '\u2717'}
                </span>
              </div>
              <h1 className="text-xl font-semibold text-ink-900 mb-2">
                {isApproved ? '삭제가 승인되었습니다' : '삭제가 거절되었습니다'}
              </h1>
              <p className="text-ink-600 mb-6">
                {isApproved
                  ? '보고서가 삭제되었으며 요청자에게 알림이 전송되었습니다.'
                  : '삭제 요청이 거절되었으며 요청자에게 알림이 전송되었습니다.'}
              </p>
              <p className="text-ink-600">이 창을 닫아도 됩니다.</p>
            </div>
          </div>
        );
      }}
      spinnerColorClass="border-red-600"
    />
  );
}
